import type { Chart, ChartOptions } from 'chart.js';
import { useMemo, useRef } from 'react';

import { CenterFlex } from '@/components/base';
import { buildTimeSeriesChartFeedbackListModalOptions } from '@/features/(charts)/chart-renderer/lib/build-time-series-chart-feedback-list-modal-options';
import chartUtils, {
  formatDisplayLabel,
} from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { createTimeSeriesPeakPinPlugin } from '@/features/(charts)/chart-renderer/lib/time-series-peak-pin-plugin';
import {
  getRenderedTimeSeriesPeakPointCandidates,
  getTimeSeriesPeakPointCandidates,
  pickTimeSeriesPeakPoints,
} from '@/features/(charts)/chart-renderer/lib/time-series-peak-point';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { LineChartCanvas, useChartTooltip } from '@/shared/ui/charts';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { getTimezoneDayjs } from '@/utils/time';

import { useOpenChartClickDialog } from '../../hooks/use-open-chart-click-dialog';
import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';
import useChartTimeSeriesData from './useChartTimeSeriesData';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const LineChart = (props: Props) => {
  const color = useColor();
  const chartRef = useRef<Chart>(null);
  const peakPointSelectionRef = useRef<{
    key: string;
    points: ReturnType<typeof pickTimeSeriesPeakPoints>;
  } | null>(null);
  const openChartClickDialog = useOpenChartClickDialog();

  const { tooltipData, chartTooltip } = useChartTooltip({
    chart: chartRef.current,
    interactive: false,
  });

  const lineChartData = useChartTimeSeriesData(props);
  const peakPointCandidates = useMemo(
    () => getTimeSeriesPeakPointCandidates(lineChartData),
    [lineChartData],
  );
  const peakPoints = useMemo(() => {
    const candidateKey = JSON.stringify(peakPointCandidates);

    if (peakPointSelectionRef.current?.key === candidateKey) {
      return peakPointSelectionRef.current.points;
    }

    const points = pickTimeSeriesPeakPoints(peakPointCandidates);
    peakPointSelectionRef.current = {
      key: candidateKey,
      points,
    };

    return points;
  }, [peakPointCandidates]);

  const dateLabels = useMemo(() => {
    if (!props.chart.target.term.unit) throw new Error('Invalid frequency');
    const termRange = chartUtils.getTimeRangeByTerm(
      getTimezoneDayjs(),
      props.chart.target.term,
    );
    return chartUtils.getDateLabelsByUnit({
      dateRange: termRange,
      unit: props.chart.target.term.unit,
    });
  }, [props.chart.target.term]);

  const compareDateLabels = useMemo(() => {
    const compareRange = chartUtils.getCompareTimeRangeByTerm(
      getTimezoneDayjs(),
      props.chart.target.term,
      props.chart.target.compare_term,
    );
    return compareRange
      ? chartUtils.getDateLabelsByUnit({
          dateRange: compareRange,
          unit: props.chart.target.term.unit,
        })
      : null;
  }, [props.chart.target.compare_term, props.chart.target.term]);

  const lineChartOptions: ChartOptions<'line'> = useMemo(() => {
    return {
      scales: {
        y: {
          min: props.chart.series[0]?.options?.scale_min || 0,
          max: props.chart.series[0]?.options?.scale_max,
          grace: '15%',
          title: {
            display: !!props.yAxisTitle,
            text: props.yAxisTitle || '',
          },
        },
        x: {
          display: true,
          position: 'bottom',
          labels: dateLabels,
          ticks: {
            callback: function (value, index) {
              const rawLabel = this.getLabelForValue(value as number);
              const frequency = chartUtils.getFrequencyByTermUnit(
                props.chart.target.term.unit,
              );
              const prevLabel = index > 0 ? dateLabels[index - 1] : null;
              return formatDisplayLabel(rawLabel, frequency, prevLabel);
            },
          },
        },
        x1: {
          display: props.chart.target.is_compare,
          position: 'top',
          labels: compareDateLabels,
          ticks: {
            callback: function (value, index) {
              if (!compareDateLabels) return '';
              const rawLabel = this.getLabelForValue(value as number);
              const frequency = chartUtils.getFrequencyByTermUnit(
                props.chart.target.term.unit,
              );
              const prevLabel = index > 0 ? compareDateLabels[index - 1] : null;
              return formatDisplayLabel(rawLabel, frequency, prevLabel);
            },
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: chartTooltip,
        },
        legend: {
          labels: {
            color: color.gray700,
          },
          position: 'right',
        },
        datalabels: {
          display: false,
        },
      },
    };
  }, [
    chartTooltip,
    color.gray700,
    compareDateLabels,
    dateLabels,
    props.chart.series,
    props.chart.target.is_compare,
    props.chart.target.term.unit,
  ]);
  const peakPinPlugin = useMemo(
    () =>
      createTimeSeriesPeakPinPlugin({
        resolvePeakPoints: (chart) =>
          pickTimeSeriesPeakPoints(
            getRenderedTimeSeriesPeakPointCandidates(chart),
          ),
        onClick: ({ dataset, dataIndex, dataLabel }) => {
          const modalOptions = buildTimeSeriesChartFeedbackListModalOptions({
            chart: props.chart,
            dashboardCardInfo: props.dashboardCardInfo,
            dataset,
            dataIndex,
            dataLabel,
            startPeakAnalysisOnFeedbackOpen: true,
          });

          openChartClickDialog({
            alias: JSON.stringify(modalOptions),
            type: DETAIL_MODAL_TYPE.CHART,
            idx: 0,
            options: modalOptions,
          });
        },
      }),
    [openChartClickDialog, props.chart, props.dashboardCardInfo],
  );

  return (
    <CenterFlex className="relative w-full flex-1 h-full">
      <LineChartCanvas
        data={lineChartData}
        options={lineChartOptions}
        plugins={[peakPinPlugin]}
        chartRef={chartRef as any}
      />
      {tooltipData && (
        <ChartCustomTooltip
          tooltip={tooltipData}
          meta={props.chart}
          tableData={props.tableData}
          dashboardCardInfo={props.dashboardCardInfo}
          peakPoints={peakPoints}
        />
      )}
    </CenterFlex>
  );
};

export default LineChart;
